import { ChangeEventHandler, FC, useState } from 'react';

import { debounce } from 'lodash';

import {
  EntertainerQuoteReview,
  ScheduleEntertainerQuotePayload,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Popup } from '@components/layout/Popup/Popup';

interface EntertainerQuotePreviewPopupContentProps<QuoteSource> {
  review: EntertainerQuoteReview<QuoteSource>;
  previewUrl: ({ quoteId, quoteFontSize }: { quoteId: string; quoteFontSize: number }) => string;
  onClose: () => void;
  onSchedule: (payload: ScheduleEntertainerQuotePayload) => void;
}

interface EntertainerQuotePreviewPopupProps<
  QuoteSource,
> extends EntertainerQuotePreviewPopupContentProps<QuoteSource> {
  isVisible: boolean;
}

const PopupContent = <QuoteSource extends unknown>({
  review,
  previewUrl,
  onClose,
  onSchedule,
}: EntertainerQuotePreviewPopupContentProps<QuoteSource>) => {
  const [quoteFontSize, setQuoteFontSize] = useState(48);
  const [isSchedulingQuote, setIsSchedulingQuote] = useState(false);

  const handleQuoteFontSizeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const debouncedFunction = debounce(() => {
      try {
        const numberValue = Number(e.target.value);
        if (isNaN(numberValue)) {
          return;
        }
        setQuoteFontSize(numberValue);
      } catch (error) {
        console.error(error);
      }
    }, 300);

    debouncedFunction();
  };

  const handleScheduleQuote = async () => {
    if (!review.quoteId) {
      return;
    }

    setIsSchedulingQuote(true);

    try {
      await onSchedule({ quoteId: review.quoteId, quoteFontSize });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSchedulingQuote(false);
    }

    onClose();
  };

  return (
    <>
      <img
        src={review.quoteId ? previewUrl({ quoteId: review.quoteId, quoteFontSize }) : ''}
        alt="Quote preview"
        style={{ display: 'block', width: '100%' }}
      />

      <>
        <FormLabel text="Quote Font Size" />
        <Input defaultValue={quoteFontSize} onChange={handleQuoteFontSizeChange} />
      </>

      <div className="mt-2">
        <Button disabled={isSchedulingQuote} onClick={handleScheduleQuote}>
          Schedule Post
        </Button>
      </div>
    </>
  );
};

export const EntertainerQuotePreviewPopup = <QuoteSource extends unknown>({
  isVisible,
  ...props
}: EntertainerQuotePreviewPopupProps<QuoteSource>) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose} height="full" title="Preview">
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
